import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowLeft, Music2, Loader2, MessageSquare } from "lucide-react";
import StatueSilhouette from "@/components/StatueSilhouette";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TIKTOK_URL = "https://www.tiktok.com/@nick_stath0";

const Success = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | error
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);
  const stoppedRef = useRef(false);

  const poll = useCallback(
    async (sid) => {
      if (stoppedRef.current) return;
      if (attemptsRef.current >= 8) {
        setStatus("error");
        return;
      }
      attemptsRef.current += 1;
      try {
        const res = await axios.get(`${API}/checkout/status/${sid}`);
        const data = res.data;
        setInfo(data);
        if (data.payment_status === "paid") {
          stoppedRef.current = true;
          setStatus("paid");
        } else if (data.status === "expired") {
          stoppedRef.current = true;
          setStatus("expired");
        } else {
          setTimeout(() => poll(sid), 2000);
        }
      } catch (e) {
        console.error(e);
        setTimeout(() => poll(sid), 2500);
      }
    },
    []
  );

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    poll(sessionId);
    return () => {
      stoppedRef.current = true;
    };
  }, [sessionId, poll]);

  return (
    <div className="relative min-h-screen bg-[#050505] text-white flex flex-col">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 flex items-center justify-center z-0"
      >
        <StatueSilhouette className="text-yellow opacity-[0.05] h-[110vh] w-auto" />
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-20">
        <div className="max-w-xl w-full text-center">
          {status === "checking" && (
            <div data-testid="status-checking">
              <Loader2 className="h-12 w-12 text-yellow animate-spin mx-auto" />
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow mt-6">
                Verifying Payment
              </div>
              <h1 className="font-display text-5xl sm:text-7xl mt-3">
                HOLD <span className="text-yellow">TIGHT</span>.
              </h1>
              <p className="text-[#a1a1aa] mt-4">
                Confirming your access to the Starter Blueprint…
              </p>
            </div>
          )}

          {status === "paid" && (
            <div data-testid="status-paid" className="fade-up">
              <CheckCircle2 className="h-14 w-14 text-yellow mx-auto" strokeWidth={2} />
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow mt-6">
                Welcome to the System
              </div>
              <h1 className="font-display text-5xl sm:text-7xl mt-3 leading-[0.9]">
                YOU'RE <span className="text-yellow">IN</span>.
              </h1>
              <p className="text-[#d4d4d8] mt-5 leading-relaxed">
                Payment confirmed{info?.amount_total ? ` — €${(info.amount_total/100).toFixed(2)}` : ""}.
                Open the private chat below — Nick will send you the{" "}
                <span className="text-yellow">Starter Blueprint</span> directly inside it.
              </p>
              <Link
                to={`/chat?session_id=${encodeURIComponent(sessionId)}`}
                data-testid="success-open-chat"
                className="btn-brutal mt-8"
              >
                <MessageSquare className="h-5 w-5" />
                Open Private Chat
              </Link>
              <div className="mt-5">
                <a
                  href={TIKTOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="success-tiktok-cta"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717a] hover:text-yellow inline-flex items-center gap-2"
                >
                  <Music2 className="h-3 w-3" /> Or DM @nick_stath0
                </a>
              </div>
            </div>
          )}

          {status === "expired" && (
            <div data-testid="status-expired">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow mt-6">
                Session Expired
              </div>
              <h1 className="font-display text-5xl sm:text-7xl mt-3">
                LET'S <span className="text-yellow">RETRY</span>.
              </h1>
              <p className="text-[#a1a1aa] mt-4">
                Your checkout session expired. No charge was made.
              </p>
              <Link to="/" className="btn-brutal mt-8" data-testid="back-home">
                <ArrowLeft className="h-5 w-5" /> Back to Blueprint
              </Link>
            </div>
          )}

          {status === "error" && (
            <div data-testid="status-error">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow mt-6">
                Something Off
              </div>
              <h1 className="font-display text-5xl sm:text-7xl mt-3">
                CHECK <span className="text-yellow">DMs</span>.
              </h1>
              <p className="text-[#a1a1aa] mt-4">
                We couldn't verify your status here, but if you were charged the
                payment will still go through. DM me on TikTok and I'll sort you out.
              </p>
              <a
                href={TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-brutal mt-8"
                data-testid="error-tiktok"
              >
                <Music2 className="h-5 w-5" /> DM @nick_stath0
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Success;
